/**
 * StructuredEditorErrorBoundary — render-time error safety net
 * usable both narrowly (around the Tiptap tree) and broadly
 * (around the whole EditorRoute). On the second pass at the
 * "page goes blank after re-transform" pilot bug we wrapped the
 * entire route in this boundary because the original narrow
 * placement was missing crashes that originated outside the
 * editor tree (header, form pane, autosave indicator, paginated
 * paper, etc.) but still unmounted the route.
 *
 * Catches:
 *   - Render-phase throws from any descendant component
 *
 * Does NOT catch:
 *   - Async errors (Promise rejections, setTimeout callbacks)
 *   - Errors thrown inside event handlers
 *   - Errors during server-side rendering
 *   For those we install window-level error + unhandledrejection
 *   listeners separately (see installGlobalErrorListeners below)
 *   that surface a fixed banner so the user has SOMETHING visible
 *   when JavaScript falls over.
 *
 * Behavior on caught error:
 *   - Logs error + componentStack to console.error
 *   - Renders a recoverable card with "Retry" + "Reload page"
 *     buttons. Retry clears the error state and re-mounts
 *     children — useful when the error was transient (a stale
 *     data pointer cleared by the next React Query refetch).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class StructuredEditorErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      '[StructuredEditorErrorBoundary] caught render-time error:',
      error,
      info,
    );
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="mx-auto my-8 max-w-2xl rounded-lg border border-terra-cotta/30 bg-white p-5 text-sm text-charcoal shadow-sm">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-terra-cotta">
          Editor crashed
        </div>
        <div className="mb-3 text-charcoal">
          The editor hit an unexpected render error. Your raw
          input and saved content are safe - only the view
          needs to be re-mounted.
        </div>
        {error.message && (
          <div className="mb-3 rounded border border-charcoal/15 bg-cloud/40 px-2 py-1.5 text-[11px] font-mono text-charcoal/70">
            {error.message}
          </div>
        )}
        {error.stack && (
          <details className="mb-3">
            <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-charcoal/60 hover:text-charcoal">
              Stack trace
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto rounded border border-charcoal/15 bg-cloud/40 p-2 text-[10px] font-mono text-charcoal/70">
              {error.stack}
            </pre>
          </details>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded border border-charcoal/20 px-3 py-1 text-xs text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Reload page
          </button>
          <button
            type="button"
            onClick={this.reset}
            className="rounded-full bg-pine px-3 py-1 text-xs text-cloud transition-colors hover:bg-pine/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
}

/**
 * Installs window-level error + unhandledrejection listeners
 * that surface a fixed-position red banner when JavaScript
 * fails outside React's render cycle. Without this, an async
 * throw from a useEffect or a Promise chain can blank the page
 * with zero visible error message. The banner shows the message
 * and exposes a Reload button so the pilot user always has at
 * least one breadcrumb when something goes wrong.
 *
 * Idempotent — calling more than once does nothing past the
 * first installation.
 */
let listenersInstalled = false;
let bannerEl: HTMLDivElement | null = null;

function showGlobalErrorBanner(message: string) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!bannerEl) {
    // Security audit M2: rebuilt the banner using createElement +
    // appendChild instead of innerHTML. Today's content was static
    // string literals so the prior innerHTML write was safe, but
    // the next maintenance edit could easily concatenate a dynamic
    // value (an error message, a meeting title, a username) into
    // the HTML blob and silently re-introduce DOM-XSS. The
    // imperative DOM API forces every dynamic insertion through
    // textContent / setAttribute, which can't execute markup.
    bannerEl = document.createElement('div');
    bannerEl.style.cssText =
      'position:fixed;left:0;right:0;bottom:0;z-index:99999;background:#7f1d1d;color:#fff;padding:10px 14px;font:13px/1.4 -apple-system,Segoe UI,Arial,sans-serif;display:flex;align-items:center;gap:12px;box-shadow:0 -2px 12px rgba(0,0,0,0.2);';

    const msgEl = document.createElement('span');
    msgEl.style.flex = '1';
    msgEl.dataset.msg = '';
    bannerEl.appendChild(msgEl);

    const reloadBtn = document.createElement('button');
    reloadBtn.dataset.reload = '';
    reloadBtn.style.cssText =
      'background:#fff;color:#7f1d1d;border:0;border-radius:4px;padding:4px 10px;font-weight:600;cursor:pointer';
    reloadBtn.textContent = 'Reload';
    reloadBtn.addEventListener('click', () => window.location.reload());
    bannerEl.appendChild(reloadBtn);

    const dismissBtn = document.createElement('button');
    dismissBtn.dataset.dismiss = '';
    dismissBtn.style.cssText =
      'background:transparent;color:#fff;border:1px solid rgba(255,255,255,0.5);border-radius:4px;padding:4px 8px;cursor:pointer';
    dismissBtn.textContent = 'Dismiss';
    dismissBtn.addEventListener('click', () => {
      if (bannerEl) {
        bannerEl.style.display = 'none';
      }
    });
    bannerEl.appendChild(dismissBtn);

    document.body.appendChild(bannerEl);
  }
  bannerEl.style.display = 'flex';
  const msgEl = bannerEl.querySelector('[data-msg]');
  if (msgEl) msgEl.textContent = `Unexpected error: ${message.slice(0, 240)}`;
}

export function installGlobalErrorListeners(): void {
  if (typeof window === 'undefined') return;
  if (listenersInstalled) return;
  listenersInstalled = true;
  window.addEventListener('error', (event) => {
    const msg = event.error?.message || event.message || 'Unknown error';
    console.error('[global error]', event.error || event.message, event);
    showGlobalErrorBanner(msg);
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const msg =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection';
    console.error('[unhandled rejection]', reason);
    showGlobalErrorBanner(msg);
  });
}
